import { MetricCard } from '../constants';

import {
  getTotalViews,
  getAverageViewTime,
  getMedianViewTime,
  getClickThroughRate,
  getUsedInJourneys,
} from '../content/metrics';

import { ContentInsight } from '../content-insight.interface';
import { formatNumber } from 'src/utils/text';

const toJourneyOpensTooltipContent = (count: number) => {
  if (count <= 0) {
    return 'This Journey has never been opened';
  }
  return `This Journey has been opened ${count} time${count !== 1 ? 's' : ''}`;
};

const toAverageViewTimeTooltipContent = (duration: string) => {

  return `Your Journeys has been viewed on average for ${duration?duration: '0'} minutes`;
};

const toCompletionRateTooltipContent = (percentage: number) => {
  if (percentage <= 0) {
    return 'This Journey has never been watched';
  }
  return `On average, recipients have watched ${percentage}% of this Journey`;
};

const toHomeCompletionRateTooltipContent = (percentage: number) => {
  if (percentage <= 0) {
    return 'This Journey has never been watched';
  }
  return `On average, recipients have watched ${percentage}% of your Journeys`;
};

const stepsViewedTooltipContent = (stepNumber: number) => {

  return `Recipients have viewed ${stepNumber} in your Journeys`;
};

export function createJourneyOpensCard(count: number, delta?: number): MetricCard {
  return {
    key: 'journey_opens',
    value: formatNumber(count),
    title: 'Journey opens',
    tooltip: toJourneyOpensTooltipContent(count),
    ...(delta && { delta: formatNumber(delta) }),
  };
}

export function createCompletionRateCard(percentage: number, delta?: number): MetricCard {
  return {
    key: 'average_completion_rate',
    value: `${percentage}%`,
    title: 'Completion rate',
    tooltip: toCompletionRateTooltipContent(percentage),
    ...(delta && { delta }),
  };
}

export function createHomeCompletionRateCard(percentage: number, delta?: number): MetricCard {
  return {
    key: 'average_home_completion_rate',
    value: `${percentage}%`,
    title: 'Average Completion rate',
    tooltip: toHomeCompletionRateTooltipContent(percentage),
    ...(delta && { delta }),
  };
}

export function createStepsCard(stepsViewed: number, delta?: number): MetricCard {
  return {
    key: 'total_number_of_steps',
    value: `${stepsViewed}`,
    title: 'Steps viewed',
    tooltip: stepsViewedTooltipContent(stepsViewed),
    ...(delta && { delta }),
  };
}

export function createAverageViewCard(duration: string, delta?: number): MetricCard {
  return {
    key: 'average_view_rate',
    value: duration? duration: '-',
    title: 'Average Time Spent',
    tooltip: toAverageViewTimeTooltipContent(duration),
    ...(delta && { delta: formatNumber(delta) }),
  };
}

export function createContentUsedInJourneysCard(contentInsight: ContentInsight): MetricCard {
  return {
    key: 'content_user_in_jurneys',
    value: getUsedInJourneys(contentInsight),
    title: 'Used in Journey',
    tooltip: 'How many times this content is used across Journeys',
  };
}

export function createContentTotalViewsCard(contentInsight: ContentInsight): MetricCard {
  return {
    key: 'total_views',
    value: getTotalViews(contentInsight),
    title: 'Total views',
    tooltip: 'Total times this content was viewed across Journeys',
  };
}

export function createContentAverageViewTimeCard(contentInsight: ContentInsight): MetricCard {
  return {
    key: 'average_time',
    value: getAverageViewTime(contentInsight),
    title: 'Average view time',
    tooltip: 'Average time spent viewing this content across Journeys',
  };
}

export function createContentMedianViewTimeCard(contentInsight: ContentInsight): MetricCard {
  return {
    key: 'median_time',
    value: getMedianViewTime(contentInsight),
    title: 'Median view time',
    tooltip: 'Median (50th percential) view time of this content across all Journeys',
  };
}

export function createContentClickThroughRateCard(contentInsight: ContentInsight): MetricCard {
  return {
    key: 'click_through_rate',
    value: getClickThroughRate(contentInsight),
    title: 'Click through rate',
    tooltip: 'How many users view this content across Journeys',
  };
}
